import React, { useEffect, useState } from "react";
import { Plus, Trash2, X, Save, Power } from "lucide-react";
import { api } from "../../lib/api";

const blank = {
  code: "",
  type: "percent",
  value: 10,
  applies_to: "order",
  product_slugs: [],
  max_uses: "",
  expires_at: "",
  active: true,
};

export const AdminDiscountsTab = ({ products }) => {
  const [list, setList] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(blank);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/admin/discounts");
      setList(r.data);
    } catch {
      setList([]);
    }
  };
  useEffect(() => { load(); }, []);

  const reset = () => { setForm(blank); setShowForm(false); setError(""); };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true); setError("");
    try {
      const payload = {
        ...form,
        code: form.code.toUpperCase().trim(),
        value: Number(form.value),
        max_uses: form.max_uses ? Number(form.max_uses) : null,
        expires_at: form.expires_at || null,
      };
      await api.post("/admin/discounts", payload);
      reset();
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed");
    } finally {
      setSubmitting(false);
    }
  };

  const toggleActive = async (d) => {
    await api.patch(`/admin/discounts/${d.code}`, { active: !d.active });
    await load();
  };

  const del = async (code) => {
    if (!window.confirm(`Delete ${code}?`)) return;
    await api.delete(`/admin/discounts/${code}`);
    await load();
  };

  const toggleProduct = (slug) => {
    const has = form.product_slugs.includes(slug);
    setForm({
      ...form,
      product_slugs: has ? form.product_slugs.filter((s) => s !== slug) : [...form.product_slugs, slug],
    });
  };

  return (
    <section data-testid="admin-discounts-section">
      <div className="flex items-center justify-between mb-4">
        <p className="font-mono text-xs tracking-[0.3em] text-kult-yellow">/ DISCOUNT CODES</p>
        <button
          onClick={() => (showForm ? reset() : setShowForm(true))}
          className="bg-kult-pink text-black px-5 py-2 font-display text-lg hover:bg-kult-yellow inline-flex items-center gap-1"
          data-testid="admin-new-discount-toggle"
        >
          {showForm ? <X size={16} /> : <Plus size={16} />} {showForm ? "CLOSE" : "NEW CODE"}
        </button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="border-2 border-kult-pink p-6 bg-kult-black2 mb-6 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="admin-new-discount-form">
          <Field label="CODE (uppercased)" required value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} testId="new-disc-code" placeholder="KULT10" />
          <Select label="TYPE" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}
            options={[{ v: "percent", l: "PERCENT %" }, { v: "fixed", l: "FIXED €" }]} testId="new-disc-type" />
          <Field label={`VALUE ${form.type === "percent" ? "(%)" : "(€)"}`} type="number" min="0" step="0.5" required value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} testId="new-disc-value" />
          <Select label="APPLIES TO" value={form.applies_to} onChange={(e) => setForm({ ...form, applies_to: e.target.value })}
            options={[{ v: "order", l: "WHOLE ORDER" }, { v: "products", l: "SPECIFIC PRODUCTS" }]} testId="new-disc-applies" />
          <Field label="MAX USES (blank = unlimited)" type="number" min="0" value={form.max_uses} onChange={(e) => setForm({ ...form, max_uses: e.target.value })} testId="new-disc-max" />
          <Field label="EXPIRES (YYYY-MM-DD, blank = never)" type="date" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} testId="new-disc-expires" />
          {form.applies_to === "products" && (
            <div className="md:col-span-2">
              <p className="font-mono text-[10px] tracking-[0.3em] text-kult-muted mb-2">PICK PRODUCTS</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-1 max-h-56 overflow-y-auto border border-kult-border p-3">
                {products.map((p) => (
                  <label key={p.slug} className="flex items-center gap-2 text-xs font-mono">
                    <input
                      type="checkbox"
                      checked={form.product_slugs.includes(p.slug)}
                      onChange={() => toggleProduct(p.slug)}
                      data-testid={`disc-prod-${p.slug}`}
                    />
                    {p.name}
                  </label>
                ))}
              </div>
            </div>
          )}
          {error && <p className="md:col-span-2 font-mono text-xs text-kult-pink">{error}</p>}
          <button type="submit" disabled={submitting} className="md:col-span-2 bg-kult-pink text-black font-display text-xl py-3 hover:bg-kult-yellow disabled:opacity-50 inline-flex items-center justify-center gap-2" data-testid="new-disc-submit">
            <Save size={18} /> {submitting ? "..." : "CREATE CODE"}
          </button>
        </form>
      )}

      <div className="border border-kult-border" data-testid="admin-discounts-list">
        <div className="grid grid-cols-12 gap-3 px-4 py-3 bg-kult-black2 border-b border-kult-border font-mono text-[10px] tracking-[0.3em] text-kult-muted">
          <span className="col-span-3">CODE</span>
          <span className="col-span-2">TYPE / VALUE</span>
          <span className="col-span-2">APPLIES</span>
          <span className="col-span-2">USES</span>
          <span className="col-span-2">EXPIRES</span>
          <span className="col-span-1 text-right">ACTION</span>
        </div>
        {list.length === 0 ? (
          <p className="px-4 py-6 font-mono text-xs text-kult-muted">No discounts yet.</p>
        ) : list.map((d) => (
          <div key={d.code} className="grid grid-cols-12 gap-3 px-4 py-3 border-b border-kult-border last:border-b-0 items-center" data-testid={`admin-disc-${d.code}`}>
            <span className="col-span-3 font-display text-xl">{d.code} {!d.active && <span className="ml-1 text-[10px] text-kult-muted">(off)</span>}</span>
            <span className="col-span-2 font-mono text-xs">
              {d.type === "percent" ? `${d.value}%` : `€${d.value.toFixed(2)}`}
            </span>
            <span className="col-span-2 font-mono text-[10px] tracking-[0.25em] text-kult-muted uppercase">
              {d.applies_to === "order" ? "ORDER" : `${d.product_slugs?.length || 0} PROD`}
            </span>
            <span className="col-span-2 font-mono text-xs">
              {d.uses_count || 0}{d.max_uses ? ` / ${d.max_uses}` : ""}
            </span>
            <span className="col-span-2 font-mono text-[11px] text-kult-muted">
              {d.expires_at ? new Date(d.expires_at).toLocaleDateString() : "—"}
            </span>
            <div className="col-span-1 flex justify-end gap-1">
              <button onClick={() => toggleActive(d)} className={`p-2 border border-kult-border hover:border-kult-pink ${d.active ? "text-kult-yellow" : "text-kult-muted"}`} aria-label="Toggle" data-testid={`disc-toggle-${d.code}`}>
                <Power size={14} />
              </button>
              <button onClick={() => del(d.code)} className="p-2 border border-kult-border hover:border-kult-pink hover:text-kult-pink" aria-label="Delete" data-testid={`disc-delete-${d.code}`}>
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

function Field({ label, testId, ...props }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] tracking-[0.3em] text-kult-muted block mb-1">{label}</span>
      <input {...props} data-testid={testId} className="w-full bg-transparent border border-kult-border px-3 py-3 font-mono text-sm focus:border-kult-pink outline-none" />
    </label>
  );
}
function Select({ label, options, testId, ...props }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] tracking-[0.3em] text-kult-muted block mb-1">{label}</span>
      <select {...props} data-testid={testId} className="w-full bg-kult-black border border-kult-border px-3 py-3 font-mono text-sm">
        {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
    </label>
  );
}
